import { TemplateType } from 'constants/templateTypes';
import { kebabCase, omit } from 'lodash';
import {
  CampaignFilters,
  CampaignFiltersKeys,
  CampaignStates,
  CampaignTypes,
} from 'modules/messagingInsights/types';
import React, { Dispatch, ReactNode, SetStateAction, useEffect, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import { BUTTON_VARIANTS } from 'ui/button';
import { Add16, Close16 } from 'ui/icons';
import { Filter } from './Filter';
import {
  StyledClose,
  StyledDropdownWrapper,
  StyledFilterRow,
  StyledItem,
  StyledOptionMenu,
  StyledSecondary,
  StyledSecondaryButton,
} from './Styled';
import { FilterOption } from './types';

type ValueNiceNames = Record<string, Record<string, string>>;

type FilterListProps = {
  setFilterOptions: Dispatch<SetStateAction<CampaignFilters>>;
  filterOptions: CampaignFilters;
  options: FilterOption[];
  children: ReactNode;
  keyNiceNames: Record<string, string>;
  valueNiceNames: ValueNiceNames;
  onFilterClose: (id?: string | null) => void;
};

const parsedVals = (niceNames: ValueNiceNames, opts: CampaignFilters, id: CampaignFiltersKeys) => {
  if (!niceNames?.[id]) {
    return opts?.[id];
  }
  return opts?.[id]?.map(
    (el: CampaignStates | CampaignTypes | TemplateType | number) => niceNames?.[id]?.[el] || el,
  );
};

export const FilterList = ({
  setFilterOptions,
  filterOptions,
  options,
  children,
  keyNiceNames = {},
  valueNiceNames = {},
  onFilterClose = () => {
    /* not empty now */
  },
}: FilterListProps) => {
  const [isShowingMenu, setIsShowingMenu] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<CampaignFiltersKeys[]>([]);
  const [contentObj, setContentObj] = useState<{
    id: CampaignFiltersKeys | null;
    replacementIdx: number;
  }>({
    id: null,
    replacementIdx: -1,
  });
  const splicedOrder = useRef<string[] | undefined>();

  useEffect(() => {
    if (filterOptions) {
      const filterOpts = Object.keys(filterOptions) as CampaignFiltersKeys[];
      if (contentObj.id && !filterOptions?.[contentObj.id]) {
        if (contentObj.replacementIdx > -1) {
          filterOpts.splice(contentObj.replacementIdx, 0, contentObj.id);
          splicedOrder.current = filterOpts;
          setSelectedFilters(filterOpts);
        } else {
          splicedOrder.current = [];
          setSelectedFilters([...filterOpts, contentObj.id]);
        }
      } else if (
        // @ts-ignore
        splicedOrder?.current?.length > 0 &&
        splicedOrder?.current?.length === filterOpts?.length
      ) {
        // @ts-ignore
        setSelectedFilters(splicedOrder?.current);
      } else {
        setSelectedFilters(filterOpts);
      }
    }
  }, [filterOptions]);

  useEffect(() => {
    if (contentObj.id && filterOptions && !filterOptions?.[contentObj.id]) {
      const filterOpts = Object.keys(filterOptions) as CampaignFiltersKeys[];
      if (contentObj.replacementIdx > -1) {
        filterOpts.splice(contentObj.replacementIdx, 0, contentObj.id);
        splicedOrder.current = filterOpts;
        setSelectedFilters(filterOpts);
      } else {
        splicedOrder.current = [];
        setSelectedFilters([...filterOpts, contentObj.id]);
      }
    }
  }, [contentObj.id]);

  const parentEl = useRef(null);
  useClickAway(parentEl, () => {
    setIsShowingMenu(false);
  });

  return (
    <div>
      {selectedFilters.map((id, idx) => {
        // @ts-ignore
        const child = React.Children.toArray(children).find((el) => el?.props?.id === id);
        return (
          <div key={`${id}-${idx}`}>
            <Filter
              id={id}
              idx={idx}
              niceName={keyNiceNames[id]}
              value={parsedVals(valueNiceNames, filterOptions, id)}
              onRemove={() => {
                setFilterOptions(omit(filterOptions, id));
              }}
              contentObj={contentObj}
              setContentObj={setContentObj}
              options={options}
              selectedFilters={selectedFilters}
              onFilterClose={onFilterClose}
            >
              {child}
            </Filter>
          </div>
        );
      })}
      {isShowingMenu ? (
        <StyledFilterRow>
          <StyledSecondary
            onClick={() => {
              /* here to not make TS angry */
            }}
          >
            Select...
          </StyledSecondary>
          <StyledClose
            variant={BUTTON_VARIANTS.GHOST}
            onClick={() => {
              setIsShowingMenu(false);
            }}
          >
            <Close16 />
          </StyledClose>
        </StyledFilterRow>
      ) : (
        <StyledSecondaryButton
          data-test="filter-list-add-filter"
          onClick={() => {
            setIsShowingMenu(true);
          }}
        >
          <Add16 />
          Add filter
        </StyledSecondaryButton>
      )}
      <StyledDropdownWrapper>
        {isShowingMenu && (
          <StyledOptionMenu ref={parentEl}>
            {options.map((el: FilterOption) => (
              <StyledItem
                data-test={`filter-label-${kebabCase(el.label)}`}
                key={el.label}
                onClick={() => {
                  setContentObj({ id: el.id as CampaignFiltersKeys, replacementIdx: -1 });
                  setIsShowingMenu(false);
                }}
              >
                {el.label}
              </StyledItem>
            ))}
          </StyledOptionMenu>
        )}
      </StyledDropdownWrapper>
    </div>
  );
};
